import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  InputBase,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Api,
  Security,
  AccountBalance,
  Person,
  Business,
  Assessment,
  CloudUpload,
  Verified,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
const StyledCard = styled(Card)(() => ({
  borderRadius: "16px",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  borderLeft: "4px solid #10b981",
  transition: "all 0.2s ease-in-out",
  cursor: "pointer",
  height: "100%",
  "&:hover": {
    boxShadow: "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-4px)",
  },
}));

const CategoryIcon = ({ category }) => {
  const iconMap = {
    KYC: <Verified />,
    Banking: <AccountBalance />,
    Identity: <Person />,
    Business: <Business />,
    Document: <CloudUpload />,
    Verification: <Security />,
    Analytics: <Assessment />,
    default: <Api />,
  };

  return iconMap[category] || iconMap.default;
};

CategoryIcon.propTypes = {
  category: PropTypes.string,
};

const CategoryCard = ({
  category,
  description,
  apiCount,
  status = "Subscription",
  onClick,
  categoryTag,
  apiData,
}) => {
  const navigate = useNavigate();

  const handleDocumentationClick = (e) => {
    e.stopPropagation();
    if (apiData) {
      navigate("/documentation", {
        state: {
          selectedSubCategory: apiData.name,
          selectedCategory: apiData.category,
          showBackButton: true,
        },
      });
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "Subscription":
        return "#fff";
      case "unsubscribed":
        return "#4B5563";
      case "beta":
        return "#f59e0b";
      case "coming-soon":
        return "#6b7280";
      default:
        return "#fff";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Subscription":
        return "Subscribed";
      case "unsubscribed":
        return "Not Activated";
      case "beta":
        return "Beta";
      case "coming-soon":
        return "Coming Soon";
      default:
        return "Subscribed";
    }
  };

  return (
    <StyledCard
      onClick={onClick}
      sx={{
        borderColor: status === "Subscription" ? "#16a249" : "#F3F4F6",
      }}
    >
      <CardContent
        className=""
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Chip
            label={getStatusLabel(status)}
            size="small"
            sx={{
              backgroundColor:
                status === "Subscription" ? "#16a249" : "#F3F4F6",
              color: getStatusColor(status),
              fontSize: "11px",
              fontWeight: 600,
              height: 24,
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 1,
                fontSize: "18px",
              }}
            >
              {category}
            </Typography>
          </Box>

          {categoryTag && (
            <Stack direction="row" spacing={1} sx={{ mt: "auto", mb: 1 }}>
              <Chip
                label={categoryTag}
                size="small"
                sx={{
                  backgroundColor: "#F1F5F9",
                  color: "#0F1729",
                  fontSize: "12px",
                  fontWeight: 700,
                  padding: "10px",
                }}
              />
            </Stack>
          )}

          <Typography
            variant="body2"
            sx={{
              fontWeight: 400,
              color: "#000",
              fontSize: "15px",
              mb: 2,
            }}
          >
            {description}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "20px" }}>
          {apiData && (
            <Typography
              variant="body1"
              onClick={handleDocumentationClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              <TextSnippetOutlinedIcon /> Documentation{" "}
              <OpenInNewOutlinedIcon />
            </Typography>
          )}
          {/* <Typography
            variant="body1"
            sx={{
              background: "#FFFFFF",
              border: "solid 1px #E1E7EF",
              padding: "8px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: "400",
            }}
          >
            Manage Subscription
          </Typography> */}
          {/* <Typography
            variant="body1"
            sx={{
              background: "#03A9F4",
              padding: "8px",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#ffffff",
            }}
          >
            Subscribe Now
          </Typography> */}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

CategoryCard.propTypes = {
  category: PropTypes.string,
  description: PropTypes.string,
  apiCount: PropTypes.number,
  status: PropTypes.string,
  onClick: PropTypes.func,
  categoryTag: PropTypes.string,
  apiData: PropTypes.shape({
    name: PropTypes.string,
    category: PropTypes.string,
  }),
};

export const ApiCatalogCards = ({
  categories,
  onCategoryClick,
  onApiClick,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");

  const renderCatalog = () => {
    if (Array.isArray(categories)) {
      const filteredApis = categories.filter((api) => {
        const categoryMatch =
          selectedCategory === "all" || api.category === selectedCategory;

        const searchMatch =
          searchQuery === "" ||
          api.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          api.category.toLowerCase().includes(searchQuery.toLowerCase());

        const subscriptionMatch =
          subscriptionFilter === "all" ||
          (subscriptionFilter === "subscribed" && api.subscribe === true) ||
          (subscriptionFilter === "unsubscribed" && api.subscribe === false);

        return categoryMatch && searchMatch && subscriptionMatch;
      });

      if (filteredApis.length === 0) {
        return (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 5 }}>
              <FilterAltOutlinedIcon
                style={{ width: "4em", height: "4em", fill: "#65758b" }}
              />
              <Typography variant="h5">No APIs found</Typography>
              <Typography
                sx={{ cursor: "pointer", color: "#01579B" }}
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                  setSubscriptionFilter("all");
                }}
                variant="h6"
              >
                Try adjusting your search criteria or filters{" "}
              </Typography>
            </Box>
          </Grid>
        );
      }

      return filteredApis
        .toSorted((a, b) => (b.subscribe === true) - (a.subscribe === true))
        .map((api) => (
          <Grid item xs={12} sm={6} md={4} key={api.id}>
            <CategoryCard
              category={api.name}
              description={api.description || "No description available"}
              status={api.subscribe ? "Subscription" : "unsubscribed"}
              onClick={() => onApiClick?.(api, api.category)}
              categoryTag={api.category}
              apiData={api}
            />
          </Grid>
        ));
    }

    return Object.entries(categories).map(([categoryName, apis]) => (
      <Grid item xs={12} sm={6} lg={4} key={categoryName}>
        <CategoryCard
          category={apis?.name}
          description={apis?.description || "Explore available APIs"}
          apiCount={apis.length}
          status={
            apis?.apiStatus === "Online" ? "Subscription" : "coming-soon"
          }
          onClick={() => onCategoryClick?.(apis)}
        />
      </Grid>
    ));
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: "#65758B",
            fontSize: "14px",
          }}
        >
          Explore our comprehensive collection of APIs organized by category
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: "20px",
          flexDirection: {
            xs: "column", 
            sm: "row", 
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            px: 2,
            py: 0.5,
            minWidth: 250,
            position: "relative",
            transition: "border 0.2s, box-shadow 0.2s",
            "&:hover": {
              border: "1px solid #bdbdbd",
              boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.04)",
            },
            "&:focus-within": {
              border: "1px solid #1976d2",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
            },
          }}
        >
          <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />
          <InputBase
            placeholder="Search APIs…"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <Box
              component="span"
              sx={{
                cursor: "pointer",
                color: "#9e9e9e",
                fontSize: "2rem",
                fontWeight: "bold",
                padding: "0 8px",
              }}
              onClick={() => setSearchQuery("")}
            >
              ×
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Select
            value={subscriptionFilter}
            onChange={(e) => {
              setSubscriptionFilter(e.target.value);
            }}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: "8px",
              Width: 250,
              bgcolor: "#fff",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: 250,
                },
              },
            }}
          >
            <MenuItem value="all">All API's</MenuItem>
            <MenuItem value="subscribed">Subscribed</MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
          </Select>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: "8px",
              Width: 250,
              bgcolor: "#fff",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: 250,
                },
              },
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {Array.isArray(categories) &&
              [...new Set(categories.map((api) => api.category))].map(
                (category) => (
                  <MenuItem
                    sx={{ whiteSpace: "normal" }}
                    key={category}
                    value={category}
                  >
                    {category}
                  </MenuItem>
                )
              )}
          </Select>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {renderCatalog()}
      </Grid>
    </Box>
  );
};

ApiCatalogCards.propTypes = {
  categories: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        category: PropTypes.string,
        description: PropTypes.string,
        subscribe: PropTypes.bool,
      })
    ),
    PropTypes.object,
  ]),
  onCategoryClick: PropTypes.func,
  onApiClick: PropTypes.func,
};